import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar, ArrowLeft, Video, Building2, Users,
  Clock, MapPin, FileText, Save, Loader2, Plus, X, Search, Mail, User, FolderKanban
} from 'lucide-react';
import ProjectSelector from '@/components/MeetingIntelligence/ProjectSelector';

const API = process.env.REACT_APP_BACKEND_URL;

const MeetingCreate = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [showAddExternal, setShowAddExternal] = useState(false);
  const [externalAttendee, setExternalAttendee] = useState({ name: '', email: '' });
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    meeting_type: searchParams.get('type') || 'physical',
    scheduled_start: '',
    scheduled_end: '',
    location: '',
    department_id: '',
    project_id: searchParams.get('project') || '',  // Check URL for project param
    attendee_ids: [],
    attendees: [], // For both selected users and external attendees
    agenda: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch users for attendee selection from meeting-intelligence endpoint
        const usersRes = await axios.get(`${API}/api/meeting-intelligence/users?token=${token}`);
        setUsers(usersRes.data.users || []);
        
        // Try to fetch departments
        try {
          const deptRes = await axios.get(`${API}/api/departments?token=${token}`);
          setDepartments(deptRes.data.departments || []);
        } catch (e) {
          console.log('Departments not available');
        }
        
        // Try to fetch projects
        try {
          const projRes = await axios.get(`${API}/api/projects?token=${token}`);
          setProjects(projRes.data.projects || []);
        } catch (e) {
          console.log('Projects not available');
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };
    fetchData();
  }, [token]);

  // Filter users based on search
  const filteredUsers = users.filter(user => {
    if (!userSearch) return true;
    const search = userSearch.toLowerCase();
    return (
      user.full_name?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search)
    );
  });

  // Add external attendee
  const addExternalAttendee = () => {
    if (!externalAttendee.name.trim() || !externalAttendee.email.trim()) {
      toast.error('Please enter both name and email');
      return;
    }
    if (!externalAttendee.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    const newAttendee = {
      id: `external-${Date.now()}`,
      name: externalAttendee.name,
      email: externalAttendee.email,
      isExternal: true
    };
    
    setFormData(prev => ({
      ...prev,
      attendees: [...prev.attendees, newAttendee]
    }));
    setExternalAttendee({ name: '', email: '' });
    setShowAddExternal(false);
    toast.success('External attendee added');
  };

  // Toggle user selection
  const toggleUserSelection = (user) => {
    const isSelected = formData.attendees.some(a => a.id === user.id);
    
    if (isSelected) {
      setFormData(prev => ({
        ...prev,
        attendees: prev.attendees.filter(a => a.id !== user.id),
        attendee_ids: prev.attendee_ids.filter(id => id !== user.id)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        attendees: [...prev.attendees, { id: user.id, name: user.full_name, email: user.email, isExternal: false }],
        attendee_ids: [...prev.attendee_ids, user.id]
      }));
    }
  };

  // Remove attendee
  const removeAttendee = (attendeeId) => {
    setFormData(prev => ({
      ...prev,
      attendees: prev.attendees.filter(a => a.id !== attendeeId),
      attendee_ids: prev.attendee_ids.filter(id => id !== attendeeId)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Meeting title is required');
      return;
    }
    
    try {
      setLoading(true);
      
      // Separate internal and external attendees
      const internalAttendees = formData.attendees.filter(a => !a.isExternal);
      const externalAttendees = formData.attendees.filter(a => a.isExternal);
      
      const payload = {
        title: formData.title,
        description: formData.description,
        meeting_type: formData.meeting_type,
        location: formData.location,
        agenda: formData.agenda,
        scheduled_start: formData.scheduled_start ? new Date(formData.scheduled_start).toISOString() : null,
        scheduled_end: formData.scheduled_end ? new Date(formData.scheduled_end).toISOString() : null,
        department_id: formData.department_id || null,
        project_id: formData.project_id || null,
        attendee_ids: internalAttendees.map(a => a.id),
        external_attendees: externalAttendees.map(a => ({ name: a.name, email: a.email }))
      };
      
      const res = await axios.post(
        `${API}/api/meeting-intelligence/meetings?token=${token}`,
        payload
      );
      
      toast.success('Meeting created successfully');
      navigate(`/meeting-intelligence/meetings/${res.data.meeting.id}`);
    } catch (err) {
      console.error('Error creating meeting:', err);
      toast.error(err.response?.data?.detail || 'Failed to create meeting');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="meeting-create">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/meeting-intelligence/meetings')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <Calendar className="h-7 w-7 text-blue-600" />
            Create Meeting
          </h1>
          <p className="text-slate-500 mt-1">Schedule a new meeting and invite attendees</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Meeting Details</CardTitle>
            <CardDescription>Basic information about the meeting</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Meeting Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="e.g., Q4 Strategy Review"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Brief description of the meeting purpose"
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Meeting Type</Label>
                <Select
                  value={formData.meeting_type}
                  onValueChange={(v) => handleInputChange('meeting_type', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="physical">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-blue-600" />
                        Physical Meeting
                      </div>
                    </SelectItem>
                    <SelectItem value="teams">
                      <div className="flex items-center gap-2">
                        <Video className="h-4 w-4 text-purple-600" />
                        Microsoft Teams
                      </div>
                    </SelectItem>
                    <SelectItem value="hybrid">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-amber-600" />
                        Hybrid
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    placeholder="e.g., Conference Room A"
                    className="pl-10"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Schedule */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-slate-600" />
              Schedule
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="scheduled_start">Start Date & Time</Label>
                <Input
                  id="scheduled_start"
                  type="datetime-local"
                  value={formData.scheduled_start}
                  onChange={(e) => handleInputChange('scheduled_start', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="scheduled_end">End Date & Time</Label>
                <Input
                  id="scheduled_end"
                  type="datetime-local"
                  value={formData.scheduled_end}
                  onChange={(e) => handleInputChange('scheduled_end', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Organization */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FolderKanban className="h-5 w-5 text-slate-600" />
              Organization
            </CardTitle>
            <CardDescription>Link to department and project</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Project Selector - Always show */}
              <div className="space-y-2">
                <Label>Project</Label>
                <ProjectSelector
                  value={formData.project_id}
                  onChange={(projectId) => handleInputChange('project_id', projectId || '')}
                  allowNone={true}
                  placeholder="Select project (optional)"
                />
                <p className="text-xs text-muted-foreground">
                  Organize this meeting under a project for better tracking
                </p>
              </div>
              
              {departments.length > 0 && (
                <div className="space-y-2">
                  <Label>Department</Label>
                  <Select
                    value={formData.department_id}
                    onValueChange={(v) => handleInputChange('department_id', v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map(dept => (
                        <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Attendees */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-slate-600" />
              Attendees
            </CardTitle>
            <CardDescription>Select team members or add external attendees</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Selected Attendees */}
            {formData.attendees.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Selected Attendees ({formData.attendees.length})</Label>
                <div className="flex flex-wrap gap-2">
                  {formData.attendees.map(attendee => (
                    <div
                      key={attendee.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                        attendee.isExternal 
                          ? 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700'
                          : 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        attendee.isExternal ? 'bg-amber-200 text-amber-800' : 'bg-blue-200 text-blue-800'
                      }`}>
                        {attendee.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{attendee.name}</span>
                        <span className="text-xs text-slate-500">{attendee.email}</span>
                      </div>
                      {attendee.isExternal && (
                        <span className="text-xs px-1.5 py-0.5 bg-amber-200 text-amber-800 rounded">External</span>
                      )}
                      <button
                        type="button"
                        onClick={() => removeAttendee(attendee.id)}
                        className="ml-1 p-1 hover:bg-slate-200 rounded-full"
                      >
                        <X className="h-3 w-3 text-slate-500" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Search and Select Users */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search team members by name or email..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setShowAddExternal(!showAddExternal)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add External
                </Button>
              </div>

              {/* Add External Attendee Form */}
              {showAddExternal && (
                <div className="p-4 border border-amber-200 bg-amber-50 dark:bg-amber-900/20 rounded-lg space-y-3">
                  <Label className="text-sm font-medium text-amber-800">Add External Attendee</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Full Name"
                        value={externalAttendee.name}
                        onChange={(e) => setExternalAttendee(prev => ({ ...prev, name: e.target.value }))}
                        className="pl-10"
                      />
                    </div>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Email Address"
                        type="email"
                        value={externalAttendee.email}
                        onChange={(e) => setExternalAttendee(prev => ({ ...prev, email: e.target.value }))}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button type="button" variant="ghost" size="sm" onClick={() => setShowAddExternal(false)}>
                      Cancel
                    </Button>
                    <Button type="button" size="sm" onClick={addExternalAttendee} className="bg-amber-600 hover:bg-amber-700">
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </div>
              )}

              {/* User List */}
              <div className="max-h-64 overflow-y-auto border rounded-lg">
                {filteredUsers.length === 0 ? (
                  <div className="p-4 text-center text-slate-500">
                    {userSearch ? 'No users found matching your search' : 'No team members available'}
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredUsers.map(user => {
                      const isSelected = formData.attendees.some(a => a.id === user.id);
                      return (
                        <div
                          key={user.id}
                          onClick={() => toggleUserSelection(user)}
                          className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${
                            isSelected
                              ? 'bg-blue-50 dark:bg-blue-900/20'
                              : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                          }`}
                        >
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                            isSelected ? 'bg-blue-500 text-white' : 'bg-slate-200 dark:bg-slate-700'
                          }`}>
                            {user.full_name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-sm">{user.full_name || 'Unknown'}</p>
                            <p className="text-xs text-slate-500">{user.email}</p>
                            {user.role && <p className="text-xs text-slate-400 capitalize">{user.role}</p>}
                          </div>
                          {isSelected && (
                            <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Agenda */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5 text-slate-600" />
              Agenda
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={formData.agenda}
              onChange={(e) => handleInputChange('agenda', e.target.value)}
              placeholder="1. Opening remarks&#10;2. Discussion topics&#10;3. Action items review&#10;4. Next steps"
              rows={6}
            />
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Button 
            type="button" 
            variant="outline"
            onClick={() => navigate('/meeting-intelligence/meetings')}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            className="bg-blue-600 hover:bg-blue-700"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Create Meeting
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default MeetingCreate;
